import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { KeyHint } from '@/components/KeyHint';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { PromptTextField } from '@/components/PromptInput';
import { ExpandedView } from './components/ExpandedView';
import {
    COMPACT_SHELL_CARD_CORNER_RADIUS_PX,
    COMPACT_SHELL_PILL_CORNER_RADIUS_PX,
    compactActionsEnterTransition,
    compactShellBorderRadiusTransition,
    compactShellLayoutTransition,
} from './queryPanelPresenceMotion';
import { useQueryPanelShake } from './hooks';
import type { QueryPanelState } from './hooks';

interface CollapsibleQueryPanelProps {
    state: QueryPanelState;
    prefix?: string;
    layoutId?: string;
}

export function CollapsibleQueryPanel({
    state,
    prefix,
    layoutId,
}: CollapsibleQueryPanelProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const prefersReducedMotion = useReducedMotion();
    const shakeRef = useQueryPanelShake(state.shakeNonce);

    const layoutGroupId = layoutId ?? 'compact-query-panel';
    const searchIconLayoutId = `${layoutGroupId}-search-icon`;
    const searchFieldLayoutId = `${layoutGroupId}-search-field`;

    const shellTransition = compactShellLayoutTransition(prefersReducedMotion);
    const actionsEnter = compactActionsEnterTransition(prefersReducedMotion);

    const deferredSectionTransition = prefersReducedMotion
        ? { duration: 0 }
        : {
              layout: shellTransition,
              opacity: actionsEnter,
              y: actionsEnter,
          };

    const filtersAndAttachmentMotion = prefersReducedMotion
        ? {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
          }
        : {
              initial: { opacity: 0, y: -12 },
              animate: { opacity: 1, y: 0 },
          };

    const actionsMotion = prefersReducedMotion
        ? {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
          }
        : {
              initial: { opacity: 0, y: 12 },
              animate: { opacity: 1, y: 0 },
          };

    const isPill = state.isMinimized;

    const shellClasses = cn(
        'relative max-w-content border border-grey-400 bg-grey-600',
        isPill
            ? cn(
                  'flex min-h-[44px] items-center',
                  'min-w-[293px] max-w-[400px]',
                  state.searchQuery ? 'w-fit' : 'w-[293px]',
                  'h-11 px-[14px] py-3',
              )
            : cn(
                  'flex w-[712px] max-w-full overflow-visible',
                  'shadow-[4px_4px_48px_rgb(7_8_10)]',
              ),
    );

    const shellBorderRadius = isPill
        ? COMPACT_SHELL_PILL_CORNER_RADIUS_PX
        : COMPACT_SHELL_CARD_CORNER_RADIUS_PX;

    const rootProps = {
        ref: shakeRef,
        role: 'search' as const,
        layout: true as const,
        className: shellClasses,
        animate: { borderRadius: shellBorderRadius },
        transition: {
            layout: shellTransition,
            borderRadius: compactShellBorderRadiusTransition(
                prefersReducedMotion,
                isPill,
            ),
        },
        ...(layoutId ? { layoutId } : {}),
    };

    const inputShared = {
        value: state.searchQuery,
        onValueChange: state.setSearchQuery,
        onKeyDown: state.handleKeyDown,
        onPaste: state.handlePaste,
        inputRef: state.inputRef,
        prefix,
        completions: state.recentQueries,
        searchIconLayoutId,
        searchFieldLayoutId,
        searchSharedLayoutTransition: shellTransition,
        onSearchFocus: state.handleFocus,
    };

    if (isPill) {
        return (
            <LayoutGroup id={layoutGroupId}>
                <motion.div {...rootProps} onPointerDown={state.focusAndExpand}>
                    <div className="flex min-w-0 flex-1 flex-row items-center">
                        <div className="relative flex min-w-0 flex-1 items-center">
                            <PromptTextField
                                {...inputShared}
                                mode="single-line"
                            />
                            <KeyHint
                                theme="dark"
                                size="single"
                                className="pointer-events-none absolute right-0 top-1/2 shrink-0 -translate-y-1/2"
                                aria-hidden
                            >
                                {t('promptInput.focusShortcut')}
                            </KeyHint>
                        </div>
                    </div>
                </motion.div>
            </LayoutGroup>
        );
    }

    return (
        <LayoutGroup id={layoutGroupId}>
            <motion.div {...rootProps}>
                <ExpandedView
                    state={state}
                    compact
                    prefix={prefix}
                    filtersWrapper={(filters) => (
                        <motion.div
                            layout
                            key="query-panel-compact-filters"
                            className="flex flex-col"
                            transition={deferredSectionTransition}
                            {...filtersAndAttachmentMotion}
                        >
                            {filters}
                        </motion.div>
                    )}
                    actionsWrapper={(actions) => (
                        <motion.div
                            layout
                            key="query-panel-compact-actions"
                            transition={deferredSectionTransition}
                            {...actionsMotion}
                        >
                            {actions}
                        </motion.div>
                    )}
                    inputLayoutProps={{
                        searchIconLayoutId,
                        searchFieldLayoutId,
                        searchSharedLayoutTransition: shellTransition,
                        onSearchFocus: state.handleFocus,
                    }}
                />
            </motion.div>
        </LayoutGroup>
    );
}
